import { useCallback, useEffect, useRef, useState } from 'react';
import { useFragment, useRelayEnvironment } from 'react-relay';
import { AboTable, PriceInfo, aboManagerFragment, commitSubscriptionMutation } from './lib';
import type { AboManagerPropsType, UpdateMessage } from './types';

const AboManager: React.FC<AboManagerPropsType> = (props) => {
  const environment = useRelayEnvironment();

  const component = useFragment(aboManagerFragment, props.component);

  const [updateMessage, setUpdateMessage] = useState<UpdateMessage | null>(null);
  const updateMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableStateRef = useRef<any>(null);
  const priceInfoStateRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (updateMessageTimeoutRef.current !== null) {
        clearTimeout(updateMessageTimeoutRef.current);
      }
    };
  }, []);

  const hideUpdateMessage = useCallback(() => {
    if (updateMessageTimeoutRef.current !== null) {
      clearTimeout(updateMessageTimeoutRef.current);
    }

    setUpdateMessage(null);
    updateMessageTimeoutRef.current = null;
  }, []);

  const showUpdateMessage = useCallback(
    (message: string, messageType: 'success' | 'failure') => {
      if (updateMessageTimeoutRef.current !== null) {
        clearTimeout(updateMessageTimeoutRef.current);
      }

      updateMessageTimeoutRef.current = setTimeout(() => hideUpdateMessage(), 4000);
      setUpdateMessage({ message, messageType });
    },
    [hideUpdateMessage],
  );

  const saveSubscriptions = useCallback(() => {
    hideUpdateMessage();

    const tableState = tableStateRef.current;
    const priceInfoState = priceInfoStateRef.current;

    if (!tableState || !priceInfoState) {
      return;
    }

    const headerDocState = tableState.refs.component.state.headerDocumentsSubscribed;
    const docState = Object.keys(tableState.refs.component.state.documentssubscribed).map((isin) => {
      const documents = tableState.refs.component.state.documentssubscribed[isin];

      return {
        trancheId: isin,
        documents: {
          ...documents,
          all: documents.all === 1,
        },
      };
    });
    docState.push({
      trancheId: '*',
      documents: headerDocState,
    });

    const preferredLanguageState = tableState.refs.component.state.preferredlanguage.toLowerCase();
    const priceInfoSubscriptionState = priceInfoState.refs.component.state.priceinfosubscribed;

    commitSubscriptionMutation(
      environment,
      {
        subscriptionManagerId: component.id,
        data: {
          documentsSubscribed: docState,
          priceInfoSubscribed: priceInfoSubscriptionState,
          preferredLanguage: preferredLanguageState,
          country: props.salesAdmission,
          retailOnly: props.retailOnly,
        },
      },
      () => {
        showUpdateMessage('Änderungen gespeichert!', 'success');
      },
      () => {
        showUpdateMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es in kürze erneut.', 'failure');
      },
    );
  }, [hideUpdateMessage, showUpdateMessage, environment, component.id, props.salesAdmission, props.retailOnly]);

  const renderUpdateMessage = () => {
    if (!updateMessage) {
      return null;
    }

    const { message, messageType } = updateMessage;
    return <div className={`mr-2.5 text-black ${messageType === 'failure' ? 'text-[#710013]' : ''}`}>{message}</div>;
  };

  return (
    <div>
      <PriceInfo ref={priceInfoStateRef} component={component.priceinfo} />
      <AboTable ref={tableStateRef} table={component.subscriptiontable} salesAdmission={props.salesAdmission} />
      <div className="w-full flex items-center justify-end max-[620px]:justify-center max-[620px]:text-center max-[620px]:[&_button]:float-none!">
        {renderUpdateMessage()}
        <button
          type="button"
          onClick={saveSubscriptions}
          className="float-right inline-block cursor-pointer rounded-[30px] border border-black bg-white px-10 py-2.5 font-normal uppercase tracking-wide text-accent outline-none my-2.5 text-sm"
        >
          {component.changesbuttontext}
        </button>
      </div>
    </div>
  );
};

export { AboManager };
